/**
 * Choosing a face: take a photo, or pick one of the drawings.
 *
 * The photo path resizes on this device before anything is sent — see
 * `lib/imageResize.ts` for why that is not the server's job.
 */

import { useEffect, useRef, useState } from 'react';
import Avatar from '@/components/Avatar';
import { api, ApiError } from '@/lib/api';
import { resizeForAvatar } from '@/lib/imageResize';

interface Profile {
  id: string;
  name: string;
  emoji: string;
  avatar: string | null;
  avatarKind: 'photo' | 'drawing' | null;
}

export default function AvatarPicker({
  profile,
  onChanged,
  onClose,
}: {
  profile: Profile;
  onChanged: (updated: Profile) => void;
  onClose: () => void;
}) {
  const [drawings, setDrawings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void api<string[]>('/api/avatars/drawings')
      .then(setDrawings)
      .catch(() => setDrawings([]));
  }, []);

  async function run(action: () => Promise<Profile>) {
    setBusy(true);
    setError(null);
    try {
      onChanged(await action());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Dat lukte niet.');
    } finally {
      setBusy(false);
    }
  }

  async function upload(file: File) {
    await run(async () => {
      const { blob, type } = await resizeForAvatar(file);
      return api<Profile>(`/api/admin/profiles/${profile.id}/avatar`, {
        method: 'POST',
        // Raw bytes with the sniffed type, not multipart and not base64.
        headers: { 'Content-Type': type },
        body: blob,
      });
    });
  }

  return (
    <div className="space-y-4 rounded-2xl border border-white/10 bg-base-800 p-4">
      <header className="flex items-center gap-3">
        <Avatar player={profile} size={56} />
        <div className="flex-1">
          <p className="font-medium">{profile.name}</p>
          <p className="text-sm text-slate-500">
            {profile.avatarKind === 'photo'
              ? 'Eigen foto'
              : profile.avatarKind === 'drawing'
                ? 'Getekende avatar'
                : 'Nog geen foto'}
          </p>
        </div>
        <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-white">
          Klaar
        </button>
      </header>

      {error && (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInput}
          type="file"
          // `capture` is left off on purpose: on a phone this still offers the
          // camera, but it also allows a photo that is already in the roll.
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
          className="min-h-12 rounded-xl bg-accent-500 px-4 font-medium text-base-950 hover:bg-accent-400 disabled:opacity-40"
        >
          Foto kiezen
        </button>

        {profile.avatar && (
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void run(() =>
                api<Profile>(`/api/admin/profiles/${profile.id}/avatar`, { method: 'DELETE' })
              )
            }
            className="min-h-12 rounded-xl bg-base-700 px-4 hover:bg-base-600 disabled:opacity-40"
          >
            Weghalen
          </button>
        )}
      </div>

      {drawings.length > 0 && (
        <div>
          <p className="mb-2 text-sm text-slate-400">Of kies een tekening:</p>
          <ul className="grid grid-cols-6 gap-2">
            {drawings.map((filename) => {
              const chosen = profile.avatar === filename;
              return (
                <li key={filename}>
                  <button
                    type="button"
                    disabled={busy}
                    aria-pressed={chosen}
                    onClick={() =>
                      void run(() =>
                        api<Profile>(`/api/admin/profiles/${profile.id}/avatar`, {
                          method: 'PUT',
                          body: JSON.stringify({ filename }),
                        })
                      )
                    }
                    className={`block w-full overflow-hidden rounded-full transition ${
                      chosen ? 'ring-2 ring-accent-400' : 'opacity-80 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={`/avatars/${filename}`}
                      alt=""
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Foto's staan op de eigen server, achter hetzelfde slot als de rest. Verwijder je een speler,
        dan verdwijnt de foto overal — ook uit oude potjes.
      </p>
    </div>
  );
}
